import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createMachineLabel, fetchMachineLabels, updateMachineLabel, deleteMachineLabel } from "../../fetchers/machineLabel";
import { Box, useTheme } from "@mui/material";
import Button from "../../components/Button";
import AddIcon from "@mui/icons-material/Add";
import { tokens } from "../../assets/theme";
import { useMsal, useAccount } from "@azure/msal-react";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import MachineLabelsGrid from "./components/machineLabelsGrid";
import CircularProgress from "@mui/material/CircularProgress";
import AxionControls from "../../components/controls/AxionControls";
import AddEditMachineLabel from "./components/machineLabelAddEditDialog";
import Popup from "../../components/Popup";
import { FormattedMessage, useIntl } from "react-intl";
import messages from "./components/messages";
import { pushErrorMessage } from '../../utils/notifications';

export default function MachineLabels() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { accounts } = useMsal();
    const account = useAccount(accounts[0]);
    const { formatMessage } = useIntl();
    const [companyKey, setCompanyKey] = useLocalStorage("companyKey", 1);
    const [openPopup, setOpenPopup] = useState(false);
    const [showBackdrop, setShowBackdrop] = useState(false);
    const [machineLabel, setMachineLabel] = useState({
        id: null,
        name: "",
        iconId: null,
        colorCode: "#FFFFFF",
        companyCode: companyKey,
    });

    const machineLabelsQuery = useQuery(["machineLabels", companyKey], () => fetchMachineLabels(companyKey),
        {
            refreshInterval: 60000,
        }
    );

    const handleEditMachineLabelClick = (id) => {
        machineLabelsQuery.refetch().then((result) => {
            const fresh = result.data.find((m) => m.id === id);
            setMachineLabel(fresh);
            setOpenPopup(true);
        });
    };

    const handleDeleteMachineLabelClick = (id) => {
        setShowBackdrop(true);
        deleteMachineLabel(id).then(() => {
            machineLabelsQuery.refetch();
            setShowBackdrop(false);
        }).catch((error) => {
            console.log(error);
            setShowBackdrop(false);
            pushErrorMessage(`Cannot delete the machine label.`);
        });
    }

    const handleChange = (event) => {
        const {id, name, value, type, checked} = event.target
        setMachineLabel({
            ...machineLabel,
            [id ? id : name]: type === "checkbox" ? checked : value
        });
    };

    const handleAddMachineLabelClick = () => {
        setMachineLabel({
            id: null,
            name: "",
            iconId: null,
            colorCode: "#FFFFFF",
            companyCode: companyKey,
        });
        setOpenPopup(true);
    };

    const handleSave = () => {
       if(machineLabel.name === "") {
            pushErrorMessage(formatMessage(messages.nameIsMandatory));
            return;
        }

        if(machineLabel.iconId === null || machineLabel.iconId === 0) {
            pushErrorMessage(formatMessage(messages.iconIsMandatory));
            return;
        }

        setShowBackdrop(true);
       let machineLabelToSave = {
            ...machineLabel,
            iconId: machineLabel.iconId === 0 ? null : machineLabel.iconId,
       };
       if (machineLabel.id !== null) {
            updateMachineLabel(machineLabelToSave).then(() => {  
                setOpenPopup(false);
                machineLabelsQuery.refetch();
                setShowBackdrop(false);
            }).catch((error) => {
                console.log(error);
                setShowBackdrop(false);
                pushErrorMessage(`Cannot update the machine label.`);
        });
       } 
       else {
            createMachineLabel(machineLabelToSave).then(() => {  
                setOpenPopup(false);
                machineLabelsQuery.refetch();
                setShowBackdrop(false);
            }).catch((error) => {
                console.log(error);
                setShowBackdrop(false);
                pushErrorMessage(`Cannot update the machine label.`);
       });
       }
    };

    if (machineLabelsQuery.isLoading)
        return <div style={{display: 'flex',  justifyContent:'center', alignItems:'center', height: '100vh'}}><CircularProgress color="secondary" /></div>;

    return (
        <Box maxWidth={1800} >
            <Button
                text={<FormattedMessage {...messages.addMachineLabelTitle} />}
                variant="outlined"
                onClick={handleAddMachineLabelClick}
                startIcon={<AddIcon />}
                sx={{
                "&:hover": {
                    backgroundColor: colors.dashbordGreen[700],
                },
                backgroundColor: colors.dashbordGreen[500],
                color: "#000000",
                position: "absolute",
                right: "10px",
                }} />
            <MachineLabelsGrid 
                machineLabels={machineLabelsQuery}
                deleteMachineLabelClick={handleDeleteMachineLabelClick}
                editMachineLabelClick={handleEditMachineLabelClick}
                />
            <Popup
                openPopup={openPopup}
                setOpenPopup={setOpenPopup}
                maxWidth="sm"
                title={machineLabel.id !== null ? <FormattedMessage {...messages.editMachineLabelTitle} /> : <FormattedMessage {...messages.addMachineLabelTitle} />}>
                <AddEditMachineLabel 
                    onChange={handleChange}
                    handleSave={handleSave}
                    machineLabel={machineLabel}
                    setOpenPopup={setOpenPopup} />
            </Popup>
            <AxionControls.AxionBackdrop open={showBackdrop} />
        </Box>
    );
}